import React, { useState, useEffect } from 'react';
import { bool, func, object, number, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, intlShape } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { LISTING_STATE_DRAFT, propTypes } from '../../../../util/types';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import css from './TopbarDesktop.module.css';
import {
  createSlug,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
} from '../../../../util/urlHelpers';
import { getUserPlan } from '../../../../util/getUserPlan';
import { FaHeart, FaLock } from 'react-icons/fa';

const SignupLink = () => {
  return (
    <NamedLink name="SignupPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink name="LoginPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

const InboxLink = ({ isClientAc, notificationCount, currentUserHasListings }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  return (
    <NamedLink
      name="InboxPage"
      className={css.topbarLink}
      params={{ tab: isClientAc ? 'orders' : 'sales' }}
      // params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
    >
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  );
};

const ProfileMenu = ({
  currentPage,
  currentUser,
  onLogout,
  isClientAc,
  id,
  slug,
  editListingLinkType,
  currentUserHasListing,
}) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountDetailsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const { hasActivePlan } = getUserPlan(currentUser) || {};

  return (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        {!isClientAc ? (
          <MenuItem key="ManageListingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </NamedLink>
          </MenuItem>
        ) : null}

        {!isClientAc ? (
          <MenuItem key="PersonalLink">
            <NamedLink
              className={css.menuLink}
              name="ProfileSettingsPage"
              to={{ hash: '#link-pessoal' }}
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.personalLink" />
            </NamedLink>
          </MenuItem>
        ) : null}

        {currentUserHasListing ? (
          <MenuItem key="EditListingPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('EditListingPage'))}
              name="EditListingPage"
              params={{ id, slug, type: editListingLinkType, tab: 'availability' }}
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.calenderAvailability" />
            </NamedLink>
          </MenuItem>
        ) : null}
        {!isClientAc ? (
          <MenuItem key="SubscriptionPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('SubscriptionPage'))}
              name="SubscriptionPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.subscription" />
            </NamedLink>
          </MenuItem>
        ) : null}
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountDetailsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountDetailsPage'))}
            name="AccountDetailsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        {!isClientAc ? (
          <MenuItem key="ActivityDashboardPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ActivityDashboardPage'), {
                // [css.disabledLink]: !hasActivePlan,
                [css.customDisabledLink]: !hasActivePlan,
              })}
              name={hasActivePlan ? 'ActivityDashboardPage' : 'SubscriptionPage'}
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.dashboard" />
              {!hasActivePlan && (
                <span className={css.iconContainer}>
                  <FaLock className={css.icon} />
                </span>
              )}
            </NamedLink>
          </MenuItem>
        ) : null}
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    notificationCount,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues,
    isClientAc,
    currentUserListing,
    currentUserListingFetched,
    isOpenPixSubAcCreated,
  } = props;
  const { hasActivePlan, plan } = getUserPlan(currentUser);
  const hasActiveSubscription = !!hasActivePlan;
  const id = currentUserListing?.id?.uuid || '00000000-0000-0000-0000-000000000000';
  const { title = '', price, state, publicData } = currentUserListing?.attributes || {};
  const slug = createSlug(title) || 'draft';
  const isDraft = state === LISTING_STATE_DRAFT;

  const editListingLinkType = isDraft
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;
  const currentUserHasListing = currentUserListing && currentUserListingFetched;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink
      isClientAc={isClientAc}
      notificationCount={notificationCount}
      currentUserHasListings={currentUserHasListings}
    />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
      isClientAc={isClientAc}
      id={id}
      slug={slug}
      editListingLinkType={editListingLinkType}
      currentUserHasListing={currentUserHasListing}
    />
  ) : null;

  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : <SignupLink />;
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;

  return (
    <nav className={classes}>
      <LinkedLogo
        className={css.logoLink}
        layout="desktop"
        alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
        linkToExternalSite={config?.topbar?.logoLink}
      />

      {/* <TopbarSearchForm
        className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
        desktopInputRoot={css.topbarSearchWithLeftPadding}
        onSubmit={onSearchSubmit}
        initialValues={initialSearchFormValues}
        appConfig={config}
      /> */}

      {/* {!!isClientAc || isOpenPixSubAcCreated ? null : (
        <CustomLinksMenu
          currentPage={currentPage}
          customLinks={customLinks}
          intl={intl}
          hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
        />
      )} */}

      <CustomLinksMenu
        currentPage={currentPage}
        customLinks={customLinks}
        intl={intl}
        isClientAc={isClientAc}
        isAuthenticated={isAuthenticated}
        isOpenPixSubAcCreated={isOpenPixSubAcCreated}
        currentUserHasListing={currentUserHasListing}
        hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
        hasActiveSubscription={hasActiveSubscription}
      />

      {inboxLinkMaybe}
      {authenticatedOnClientSide && isClientAc && (
        <NamedLink
          name="FavoritesPage"
          className={classNames(css.topbarLink, {
            [css.currentPage]: currentPage === 'FavoritesPage',
          })}
        >
          <span className={classNames(css.topbarLinkLabel, css.favoritesLinkLabel)}>
            <FormattedMessage id="SearchPage.favorites" />
            <FaHeart className={css.favoritesIcon} aria-hidden="true" />
          </span>
        </NamedLink>
      )}
      {profileMenuMaybe}
      {signupLinkMaybe}
      {loginLinkMaybe}
    </nav>
  );
};

TopbarDesktop.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  currentPage: null,
  notificationCount: 0,
  initialSearchFormValues: {},
  config: null,
};

TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  onLogout: func.isRequired,
  notificationCount: number,
  onSearchSubmit: func.isRequired,
  initialSearchFormValues: object,
  intl: intlShape.isRequired,
  config: object,
};

export default TopbarDesktop;
